import type { ComponentType } from "react";
import { motion } from "framer-motion";
import type { Variants } from "framer-motion";
import type { Symptom } from "@/components/site/SymptomGrid";
import { gapContainer, gapItem } from "@/components/site/gap-motion";

type IconType = ComponentType<{
  className?: string;
  strokeWidth?: number;
  "aria-hidden"?: boolean | "true" | "false";
}>;

export type PlainDividedItem = Symptom & { icon?: IconType };

/**
 * Light-background divided list: no cards, no numbers, thin vertical
 * divider to the left of each item. Optional icon badge above the title.
 */
export function PlainDividedList({
  items,
  itemVariants = gapItem,
  bodyClassName = "text-base",
}: {
  items: readonly PlainDividedItem[];
  itemVariants?: Variants;
  /** Size classes for item body text; defaults to text-base. */
  bodyClassName?: string;
}) {
  const cols = items.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4";
  return (
    <motion.div
      variants={gapContainer}
      className={`grid gap-y-10 sm:grid-cols-2 ${cols} lg:gap-x-0`}
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <motion.div
            key={item.title}
            variants={itemVariants}
            className="border-l border-border pl-6 pr-6 lg:pl-7 lg:pr-8"
          >
            {Icon && (
              <Icon className="mb-3 h-20 w-20 text-ink" strokeWidth={0.5} aria-hidden="true" />
            )}
            <h3
              className="text-xl font-semibold leading-snug text-ink"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {item.title}
            </h3>
            <p
              className={`mt-3 leading-relaxed text-muted-foreground ${bodyClassName}`}
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {item.body}
            </p>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
